"use client"

import { PlayheadReadout, PlayheadSeek, type PlayheadStore } from "@/components/playhead"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import { VROverlay } from "@/components/vr/vr-overlay"
import { ChevronLeft, ChevronRight, Pause, Play, Repeat2, Settings, Volume2, VolumeX, ZoomIn } from "@/lib/icons"
import { type CommunityScript, type UserScript } from "@/lib/script-store"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"
import type React from "react"

// Extracted from components/booru-detail-modal.tsx (pure move: same JSX, same behaviour).
interface VideoControlsOverlayProps {
  abLoopEnabled: boolean
  activeVideoUrl: string | null
  currentAlbumPage: number
  duration: number
  formatTime: (seconds: number) => string
  frameNavEnabled: boolean
  getCurrentTime: () => number
  handleAlbumNextPage: () => void
  handleAlbumPrevPage: () => void
  handleFrameBackward: (e: React.MouseEvent) => void
  handleFrameForward: (e: React.MouseEvent) => void
  handlePlayPause: () => void
  handleVrScriptSelect: (script: UserScript) => void
  isAlbum: boolean
  isLooping: boolean
  isMuted: boolean
  isPlaying: boolean
  isVideoZoomEnabled: boolean
  loopPointA: number | null
  loopPointB: number | null
  playbackSpeed: number
  playhead: PlayheadStore
  setAbLoopEnabled: React.Dispatch<React.SetStateAction<boolean>>
  setCurrentTime: (t: number) => void
  setFrameNavEnabled: React.Dispatch<React.SetStateAction<boolean>>
  setIsLooping: React.Dispatch<React.SetStateAction<boolean>>
  setIsMuted: React.Dispatch<React.SetStateAction<boolean>>
  setIsVideoZoomEnabled: React.Dispatch<React.SetStateAction<boolean>>
  setLoopPointA: React.Dispatch<React.SetStateAction<number | null>>
  setLoopPointB: React.Dispatch<React.SetStateAction<number | null>>
  setPanX: React.Dispatch<React.SetStateAction<number>>
  setPanY: React.Dispatch<React.SetStateAction<number>>
  setPlaybackSpeed: React.Dispatch<React.SetStateAction<number>>
  setShowVolumePopup: React.Dispatch<React.SetStateAction<boolean>>
  setVideoPitchCorrection: (enabled: boolean) => void
  setVolume: React.Dispatch<React.SetStateAction<number>>
  setVrSyncDelay: React.Dispatch<React.SetStateAction<number>>
  setZoom: React.Dispatch<React.SetStateAction<number>>
  showVideoHud: boolean
  showVolumePopup: boolean
  totalAlbumPages: number
  userChangedMuteRef: React.RefObject<boolean>
  videoHudBottomPx: number
  videoPitchCorrection: boolean
  videoRef: React.RefObject<HTMLVideoElement | null>
  viewingAdSlot: number | null
  volume: number
  volumePopupRef: React.RefObject<HTMLDivElement | null>
  vrActiveScript: UserScript | CommunityScript | null
  vrSyncDelay: number
}

export function VideoControlsOverlay({
  abLoopEnabled,
  activeVideoUrl,
  currentAlbumPage,
  duration,
  formatTime,
  frameNavEnabled,
  getCurrentTime,
  handleAlbumNextPage,
  handleAlbumPrevPage,
  handleFrameBackward,
  handleFrameForward,
  handlePlayPause,
  handleVrScriptSelect,
  isAlbum,
  isLooping,
  isMuted,
  isPlaying,
  isVideoZoomEnabled,
  loopPointA,
  loopPointB,
  playbackSpeed,
  playhead,
  setAbLoopEnabled,
  setCurrentTime,
  setFrameNavEnabled,
  setIsLooping,
  setIsMuted,
  setIsVideoZoomEnabled,
  setLoopPointA,
  setLoopPointB,
  setPanX,
  setPanY,
  setPlaybackSpeed,
  setShowVolumePopup,
  setVideoPitchCorrection,
  setVolume,
  setVrSyncDelay,
  setZoom,
  showVideoHud,
  showVolumePopup,
  totalAlbumPages,
  userChangedMuteRef,
  videoHudBottomPx,
  videoPitchCorrection,
  videoRef,
  viewingAdSlot,
  volume,
  volumePopupRef,
  vrActiveScript,
  vrSyncDelay,
}: VideoControlsOverlayProps) {
  const tt = useTranslations("booruDetailModal")
  return (
    <>
      {showVideoHud && (
                <div
                  className="absolute left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-3 z-20"
                  style={{ bottom: videoHudBottomPx }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-1.5 mb-2 w-full">
                    <div className={cn("flex items-center gap-1.5", !isAlbum && "flex-1")}>
                      <div ref={volumePopupRef} className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            "h-[34px] w-[34px] p-0 rounded-full transition-all duration-150",
                            showVolumePopup
                              ? "text-white bg-white/20 scale-95"
                              : "text-white/80 hover:text-white hover:bg-white/10",
                            "active:scale-90"
                          )}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setShowVolumePopup((v) => !v)
                          }}
                          title={tt("volume")}
                        >
                          {isMuted || volume <= 0 ? (
                            <VolumeX className="h-4 w-4" />
                          ) : (
                            <Volume2 className="h-4 w-4" />
                          )}
                        </Button>
                        {showVolumePopup && (
                          <div
                            className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-black/90 rounded-lg px-3 py-3 border border-white/20 shadow-xl"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex flex-col items-center gap-2">
                              <div className="text-xs text-white/70 font-medium">
                                {Math.round((isMuted ? 0 : volume) * 100)}%
                              </div>
                              {/* Radix Slider (same component used app-wide, purple bg-primary
                                  thumb/track) instead of a native input[type=range] rotated via
                                  writing-mode — Chromium dropped reliable vertical rendering for
                                  that hack, which is what made the old control render squished
                                  and overflow the popup. */}
                              <Slider
                                orientation="vertical"
                                min={0}
                                max={1}
                                step={0.01}
                                value={[isMuted ? 0 : volume]}
                                onValueChange={([newVolume]) => {
                                  userChangedMuteRef.current = true
                                  setVolume(newVolume)
                                  const shouldMute = !(newVolume > 0)
                                  setIsMuted(shouldMute)
                                  if (videoRef.current) {
                                    videoRef.current.volume = newVolume
                                    videoRef.current.muted = shouldMute
                                  }
                                }}
                                className="h-24"
                                aria-label={tt("volume")}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-[34px] w-[34px] rounded-full transition-all duration-150",
                          isPlaying
                            ? "text-white bg-white/20"
                            : "text-white/80 hover:text-white hover:bg-white/10",
                          "active:scale-90"
                        )}
                        onClick={handlePlayPause}
                        title={tt("playPauseSpace")}
                      >
                        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-[34px] w-[34px] rounded-full transition-all duration-150",
                          isLooping
                            ? "text-green-400 bg-green-500/20 hover:bg-green-500/30"
                            : "text-white/80 hover:text-white hover:bg-white/10",
                          "active:scale-90"
                        )}
                        onClick={() => setIsLooping(!isLooping)}
                        title={tt("loop")}
                      >
                        <Repeat2 className="h-4 w-4" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-[34px] w-[34px] rounded-full transition-all duration-150",
                          isVideoZoomEnabled
                            ? "text-amber-400 bg-amber-500/20 hover:bg-amber-500/30"
                            : "text-white/80 hover:text-white hover:bg-white/10",
                          "active:scale-90"
                        )}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          if (isVideoZoomEnabled) {
                            setIsVideoZoomEnabled(false)
                            setZoom(1)
                            setPanX(0)
                            setPanY(0)
                          } else {
                            setIsVideoZoomEnabled(true)
                          }
                        }}
                        title={isVideoZoomEnabled ? tt("exitZoomMode") : tt("enterZoomMode")}
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>

                      {/* VR button — shown for video posts only; always available so user can try even if not auto-detected */}
                      {!isAlbum && activeVideoUrl && (
                        <VROverlay
                          compact
                          videoRef={videoRef}
                          contentUrl={activeVideoUrl}
                          syncDelay={vrSyncDelay}
                          onSyncDelayChange={setVrSyncDelay}
                          onSeek={setCurrentTime}
                          activeScript={vrActiveScript}
                          onScriptSelect={handleVrScriptSelect}
                          // No onScriptClear — global-playback-store has no "clear the
                          // current script back to null" action to call (setScript requires
                          // a script, setPattern is the only place currentScript gets
                          // nulled). VROverlay's Clear pill only renders when this prop is
                          // given, so omitting it just hides that button rather than
                          // wiring to the wrong thing.
                        />
                      )}
                    </div>

                    {isAlbum && (
                      <div className="flex-1 flex items-center justify-center gap-1.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                          onClick={handleAlbumPrevPage}
                          disabled={viewingAdSlot === null && currentAlbumPage === 0}
                          title={tt("previousPage2")}
                        >
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </Button>
                        <div className="text-[11px] text-white/70 tabular-nums min-w-[3.25rem] text-center">
                          {viewingAdSlot !== null ? "Ad" : `${currentAlbumPage + 1}/${totalAlbumPages}`}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                          onClick={handleAlbumNextPage}
                          disabled={viewingAdSlot === null && currentAlbumPage === totalAlbumPages - 1}
                          title={tt("nextPage2")}
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}

                    {/* A-B Loop Controls (shown when enabled) */}
                    {abLoopEnabled && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs bg-black/60 border-white/20 text-white hover:bg-white/10"
                          onClick={(e) => {
                            e.stopPropagation()
                            const nowT = getCurrentTime()
                            setLoopPointA(nowT)
                            if (loopPointB !== null && nowT >= loopPointB) {
                              setLoopPointB(null)
                            }
                          }}
                          disabled={!duration}
                          title={tt("setLoopStartPoint")}
                        >
                          A
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs bg-black/60 border-white/20 text-white hover:bg-white/10"
                          onClick={(e) => {
                            e.stopPropagation()
                            const nowT = getCurrentTime()
                            if (loopPointA !== null && nowT > loopPointA) {
                              setLoopPointB(nowT)
                            }
                          }}
                          disabled={!duration || loopPointA === null}
                          title={tt("setLoopEndPoint")}
                        >
                          B
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-white/80 hover:bg-white/10"
                          onClick={(e) => {
                            e.stopPropagation()
                            console.log('[A-B Loop] Clearing loop points')
                            setLoopPointA(null)
                            setLoopPointB(null)
                          }}
                          disabled={loopPointA === null && loopPointB === null}
                          title={tt("clearLoopPoints")}
                        >
                          {tt("clear")}
                        </Button>
                        <div className="w-px h-6 bg-white/20 mx-1" />
                      </>
                    )}

                    {/* Frame Navigation Controls (shown when enabled) */}
                    {frameNavEnabled && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs bg-black/60 border-white/20 text-white hover:bg-white/10"
                          onClick={handleFrameBackward}
                          disabled={!duration}
                          title={tt("previousFrame")}
                        >
                          <ChevronLeft className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs bg-black/60 border-white/20 text-white hover:bg-white/10"
                          onClick={handleFrameForward}
                          disabled={!duration}
                          title={tt("nextFrame")}
                        >
                          <ChevronRight className="h-3 w-3" />
                        </Button>
                        <div className="w-px h-6 bg-white/20 mx-1" />
                      </>
                    )}

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "h-[34px] w-[34px] rounded-full transition-all duration-150",
                            "text-white/80 hover:text-white hover:bg-white/10",
                            "active:scale-90"
                          )}
                          title={tt("playbackSettings")}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="end"
                        side="top"
                        className="w-64 bg-black/90 border-white/20 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="space-y-4">
                          <h4 className="font-medium text-sm text-white">{tt("playbackSettings")}</h4>

                          <div className="space-y-2">
                            <Label className="text-sm text-white/80">{tt("playbackSpeed")}</Label>
                            <Slider
                              value={[playbackSpeed]}
                              onValueChange={([value]) => {
                                setPlaybackSpeed(value)
                                if (videoRef.current) {
                                  videoRef.current.playbackRate = value
                                }
                              }}
                              min={0.25}
                              max={3}
                              step={0.25}
                              className="w-full"
                            />
                            <div className="text-xs text-white/60 text-center">
                              {tt("playbackSpeedX", { playbackSpeed })}
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <Label htmlFor="booru-ab-loop" className="text-sm text-white/80">
                              {tt("aBLoop")}
                            </Label>
                            <Switch
                              id="booru-ab-loop"
                              checked={abLoopEnabled}
                              onCheckedChange={setAbLoopEnabled}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <Label htmlFor="booru-frame-nav" className="text-sm text-white/80">
                              {tt("frameNavigation")}
                            </Label>
                            <Switch
                              id="booru-frame-nav"
                              checked={frameNavEnabled}
                              onCheckedChange={setFrameNavEnabled}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <Label htmlFor="booru-preserve-pitch" className="text-sm text-white/80">
                              {tt("preservePitch")}
                            </Label>
                            <Switch
                              id="booru-preserve-pitch"
                              checked={videoPitchCorrection}
                              onCheckedChange={(checked) => {
                                setVideoPitchCorrection(checked)
                                if (videoRef.current) {
                                  videoRef.current.preservesPitch = checked
                                }
                              }}
                            />
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="flex items-center gap-2 w-full mb-1">
                    <div className="text-xs text-white/60 tabular-nums min-w-[4.75rem] text-left">
                      <PlayheadReadout store={playhead} duration={duration} />
                    </div>
                    <div className="flex-1 relative">
                      <PlayheadSeek
                        store={playhead}
                        duration={duration}
                        ariaLabel={tt("seek")}
                        onSeek={(nextTime) => {
                          if (videoRef.current) videoRef.current.currentTime = nextTime
                          setCurrentTime(nextTime)
                        }}
                      />
                      {/* A-B Loop visual markers */}
                      {abLoopEnabled && duration > 0 && (
                        <>
                          {/* Highlighted loop region (only when both points are set) */}
                          {loopPointA !== null && loopPointB !== null && (
                            <div
                              className="absolute top-1/2 -translate-y-1/2 h-1 bg-green-500/30 pointer-events-none"
                              style={{
                                left: `${(loopPointA / duration) * 100}%`,
                                width: `${((loopPointB - loopPointA) / duration) * 100}%`,
                              }}
                            />
                          )}
                          {/* Point A marker */}
                          {loopPointA !== null && (
                            <div
                              className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-green-500 pointer-events-none"
                              style={{ left: `${(loopPointA / duration) * 100}%` }}
                              title={`A: ${formatTime(loopPointA)}`}
                            />
                          )}
                          {/* Point B marker */}
                          {loopPointB !== null && (
                            <div
                              className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-red-500 pointer-events-none"
                              style={{ left: `${(loopPointB / duration) * 100}%` }}
                              title={`B: ${formatTime(loopPointB)}`}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>

                </div>
              )}
    </>
  )
}
